import React from "react";
import { Box, Typography, Stack, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { tokens } from "../../theme";
import { RootState } from "../../store/Index";
import muzukurulogo from "../../assets/muzukurublack.png";

const SECTIONS: { title: string; body: React.ReactNode }[] = [
  {
    title: "1. Your Right to Data Deletion",
    body: (
      <>
        You may request the deletion of your personal information collected
         by Muzukuru Streaming Services (&quot;Muzukuru&quot;, &quot;we&quot;, &quot;us&quot;, &quot;our&quot;) at any
        time. This includes information tied to your account, as well as
        information shared with us through connected services such as
        WhatsApp.
      </>
    ),
  },
  {
    title: "2. How to Request Deletion",
    body: (
      <>
        To request deletion of your data, do one of the following:
        <Box component="ul" sx={{ pl: 3, mt: 1 }}>
          <li>
            Email us at{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              privacy@muzukuru.com
            </Box>{" "}
             with the subject line &quot;Data Deletion Request&quot;, using the email
            address or phone number associated with your account.
          </li>
          <li>
            Message us directly through WhatsApp using the number you
            contacted us from, and request account/data deletion.
          </li>
          <li>
            If you have an account, contact your account representative and
            ask them to submit the request on your behalf.
          </li>
        </Box>
      </>
    ),
  },
  {
    title: "3. What Gets Deleted",
    body: (
      <>
        Upon a verified request, we will delete or anonymize:
        <Box component="ul" sx={{ pl: 3, mt: 1 }}>
          <li>Your account profile and login credentials.</li>
          <li>Personal contact information (name, email, phone number).</li>
          <li>
            Messages and conversation history exchanged with us through
            WhatsApp or other connected messaging channels.
          </li>
        </Box>
      </>
    ),
  },
  {
    title: "4. What May Be Retained",
    body: (
      <>
        Some information may be retained after a deletion request where
        required for legitimate business or legal purposes, including:
        <Box component="ul" sx={{ pl: 3, mt: 1 }}>
          <li>
            Booking, payment, and invoicing records, which we may be legally
            required to retain for accounting or tax purposes.
          </li>
          <li>
            Memorial pages, livestream recordings, or tributes tied to a
            booking made by a client, where deletion would remove content
            belonging to or shared with other individuals (e.g. a family
            memorial page) — in these cases, contact us directly and we will
            work with you on an appropriate resolution.
          </li>
          <li>
            Information we are required to retain to comply with a legal
            obligation, resolve a dispute, or enforce our agreements.
          </li>
        </Box>
      </>
    ),
  },
  {
    title: "5. Processing Time",
    body: (
      <>
        We will process verified deletion requests within 30 days. We may
        contact you to confirm your identity before completing the request,
        to prevent unauthorized deletion of your data.
      </>
    ),
  },
  {
    title: "6. Related Policies",
    body: (
      <>
        For more information on how we collect and use your data, see our{" "}
        <Box component="span" sx={{ fontWeight: 700 }}>
          Privacy Policy
        </Box>
        .
      </>
    ),
  },
];

const DataDeletion: React.FC = () => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: colors.primary[400], minHeight: "100vh" }}>
      <Box sx={{ maxWidth: "860px", mx: "auto", px: { xs: 2, sm: 3, lg: 4 }, py: { xs: 6, md: 10 } }}>
        <Box
          component="img"
          onClick={() => navigate("/")}
          src={muzukurulogo}
          alt="Muzukuru logo"
          sx={{ width: { xs: "140px", sm: "160px" }, height: "auto", cursor: "pointer", mb: 5 }}
        />

        <Typography
          sx={{
            fontSize: { xs: "1.75rem", md: "2.25rem" },
            fontWeight: 800,
            color: colors.primary[900],
            mb: 1,
          }}
        >
          User Data Deletion
        </Typography>
        <Typography sx={{ fontSize: "0.875rem", color: `${colors.primary[900]}70`, mb: 5 }}>
          Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </Typography>

        <Stack divider={<Divider sx={{ borderColor: colors.primary[100] }} />} spacing={4}>
          {SECTIONS.map((section) => (
            <Box key={section.title}>
              <Typography
                sx={{
                  fontSize: "1.125rem",
                  fontWeight: 700,
                  color: colors.primary[900],
                  mb: 1.5,
                }}
              >
                {section.title}
              </Typography>
              <Typography
                component="div"
                sx={{
                  fontSize: "0.9375rem",
                  lineHeight: 1.7,
                  color: `${colors.primary[900]}90`,
                }}
              >
                {section.body}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default DataDeletion;
